import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import { Collapse } from "@mui/material";
import ModalBox from "components/Modal";
import AutocompleteFormComponent, { renderOption } from "components/shared/FormComponent/AutocompleteFormComponent";
import TextFormComponent from "components/shared/FormComponent/TextFormComponent";
import StyledButton from "components/shared/StyledButton";
import { socialProfileList } from "containers/NewBeneficiaryContainer";
import { Body, BodyHighlight } from "helpers/TypographyComponents";
import { useEffect, useState } from "react";
import type { SubmitHandler } from "react-hook-form";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import type { TEntity } from "store/beneficiaryStore";
import { entityList, getEntityNames, fetchCreateBeneficiary, sendInvitation } from "store/beneficiaryStore";
import type { Currencies } from "store/settingsStore";
import { useAppDispatch, useAppSelector } from "store/store";
import type { TFetchStatus } from "store/types";

import styles from "./style.module.scss";

/**
 * Component usage : We create a new beneficiary with the minimal of informations
 * required informations values are replaced by "draft" to catch that field has to be completed by the author (on his space)
 */
interface IInviteAuthorModal {
    open: boolean;
    onClose: () => void;
    /**
     * Company currency, used for default value of statementCurrency key
     */
    companyCurrency: Currencies | undefined;
}

type TFormInviteAuthor = {
    name: string;
    surname: string;
    email: string;
    entity: TEntity;
};

const InviteAuthorModal = ({ open, onClose, companyCurrency }: IInviteAuthorModal) => {
    const [t] = useTranslation();
    const entityNames = getEntityNames(t);
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const [invitingStatus, setInvitingStatus] = useState<TFetchStatus>("idle");

    const defaultFormValues: TFormInviteAuthor = {
        name: "",
        surname: "",
        email: "",
        entity: "physical",
    };

    const methods = useForm({
        defaultValues: defaultFormValues,
        mode: "all",
    });

    const {
        control,
        handleSubmit,
        watch,
        reset,
        formState: { errors },
    } = methods;

    const isLegalEntity = watch("entity") === "legal";
    const authorName = `${watch("name")} ${watch("surname")?.toUpperCase()}`;

    useEffect(() => {
        reset();
        setInvitingStatus("idle");
    }, [open]);

    const onSubmit: SubmitHandler<TFormInviteAuthor> = async (form) => {
        setInvitingStatus("pending");
        const req = {
            ...form,
            // default values for reqquired field
            socialProfileType: form.entity === "physical" ? socialProfileList[0][0].type : socialProfileList[0][5].type,
            accountingTypology: "Par défaut",
            siretNumber: "",
            street: "draft",
            zipCode: "draft",
            phoneNumber: "draft",
            city: "draft",
            socialSecurityNumber: form.entity === "physical" ? "draft" : "",
            vat: 0,
            statementCurrency: companyCurrency,
            country: "France",
        };

        const response = await dispatch(fetchCreateBeneficiary(req));
        if (response.meta.requestStatus === "fulfilled") {
            const newBeneficiar = response.payload.data;
            await dispatch(
                sendInvitation({
                    id: newBeneficiar.id,
                    email: newBeneficiar.email,
                    name: `${newBeneficiar.name.trim() ?? ""} ${newBeneficiar.surname.trim() ?? ""}`,
                    needsUpdate: true,
                }),
            );
            setInvitingStatus("succeeded");
        }

        if (response.meta.requestStatus === "rejected") setInvitingStatus("failed");
    };

    const handleCloseSucceedModal = () => {
        if (invitingStatus !== "succeeded") {
            return;
        } else {
            onClose();
            setInvitingStatus("idle");
        }
    };

    useEffect(() => {
        if (invitingStatus === "succeeded") {
            navigate("/beneficiars");
        }
    }, [invitingStatus]);

    return (
        <ModalBox
            open={open}
            onClose={onClose}
            style={{ width: "460px" }}
            mainTitle={t("Beneficiars.InviteNewAuthorToComplete")}
            subTitle={t("Beneficiars.InviteAuthorSubtitle")}
        >
            <form className={styles["invite-author-modal"]} onSubmit={handleSubmit(onSubmit)}>
                {invitingStatus !== "succeeded" && (
                    <div className={styles["invite-author-modal-fields"]}>
                        <AutocompleteFormComponent
                            label={t("NewBeneficiary.Entity")}
                            control={control}
                            controlName="entity"
                            options={entityList}
                            renderOption={(props, option) => renderOption(props, option, entityNames)}
                            renderOptionText={(option) => entityNames[option]}
                            isOptionEqualToValue={(option, value) => option === value}
                            error={errors.entity}
                            required
                        />
                        <Collapse in={!isLegalEntity} timeout={"auto"} unmountOnExit>
                            <TextFormComponent label={t("NewBeneficiary.Name")} control={control} controlName="name" />
                        </Collapse>
                        <TextFormComponent
                            label={isLegalEntity ? t("NewBeneficiary.CompanyName") : t("NewBeneficiary.Surname")}
                            control={control}
                            controlName="surname"
                            required
                            error={errors.surname}
                        />
                        <TextFormComponent
                            label={t("NewBeneficiary.Email")}
                            control={control}
                            controlName="email"
                            placeholder="email@crealo.app"
                            required
                            error={errors.email}
                        />
                    </div>
                )}
                {invitingStatus === "succeeded" && (
                    <div className={styles["invite-author-modal-succeeded-content"]}>
                        <div>
                            <BodyHighlight color="success">
                                {t("NewBeneficiary.InvitationHasBeenSentTo", { authorName })}
                            </BodyHighlight>
                            <Body>{t("NewBeneficiary.InformationAddedInCrealo")}</Body>
                        </div>
                        <div className={styles["invite-author-modal-succeeded-list"]}>
                            <li>{t("NewBeneficiary.AuthorProfileCreated")}</li>
                            <li>{t("NewBeneficiary.AuthorEmailSent")} </li>
                        </div>
                    </div>
                )}
                <div className={styles["invite-author-modal-buttons"]}>
                    <StyledButton
                        buttonType={invitingStatus === "succeeded" ? "primary" : "outlined"}
                        fullWidth
                        type={invitingStatus === "idle" ? "submit" : undefined}
                        onClick={handleCloseSucceedModal}
                        loading={invitingStatus === "pending"}
                    >
                        {invitingStatus !== "succeeded" && <EmailOutlinedIcon sx={{ height: "16px", width: "16px" }} />}
                        {invitingStatus === "succeeded" ? "Fermer" : t("Beneficiars.InviteTheNewAuthor")}
                    </StyledButton>
                </div>
            </form>
        </ModalBox>
    );
};

export default InviteAuthorModal;
